import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiClient, ApiError } from "./api/client.js";

/**
 * Chi e' collegato, e il client che porta il suo cookie.
 *
 * La sessione **non sta in localStorage**: sta in un cookie httpOnly che il
 * browser manda da solo e che JavaScript non puo' leggere. Qui dentro c'e'
 * solo l'utente, cioe' l'esito di `auth.me`. Tenerne una copia altrove
 * vorrebbe dire due verita' che divergono: la pagina crede di essere dentro
 * mentre il server risponde 401, e l'operatore vede una schermata ferma.
 */

export interface User {
  id: string;
  username: string;
  displayName: string;
  role: string;
  level: number;
  /** Quello che QUESTO utente puo' fare, non la tabella dei ruoli. Serve a
   *  nascondere i comandi che non ha — come fa l'app desktop — invece di
   *  mostrarli e lasciarli rispondere 403 quando li preme. Il controllo vero
   *  resta sulla rotta: questo decide solo cosa si vede. */
  permissions?: Record<string, boolean>;
}

type Phase =
  /** Non sappiamo ancora se c'e' una sessione: si sta chiedendo al server. */
  | { kind: "loading" }
  /** Database vuoto: si crea il primo amministratore. */
  | { kind: "bootstrap" }
  | { kind: "anonymous" }
  | { kind: "authenticated"; user: User };

interface SessionValue {
  phase: Phase;
  api: ApiClient;
  login: (username: string, password: string) => Promise<void>;
  bootstrap: (username: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Rilegge lo stato dal server. Da chiamare quando qualcosa suggerisce che
   *  la sessione non e' piu' quella che crediamo. */
  refresh: () => Promise<void>;
  /** Vero se l'utente collegato ha il permesso. Sconosciuto = falso: davanti a
   *  un impianto un bottone in piu' che risponde 403 confonde piu' di uno in
   *  meno. */
  can: (key: string) => boolean;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  // Il client vive in un ref: ricrearlo a ogni render farebbe ripartire il
  // WebSocket della telemetria a ogni cambio di stato.
  const apiRef = useRef<ApiClient>(undefined);
  if (!apiRef.current) {
    apiRef.current = new ApiClient({
      // Una sessione scaduta in mezzo a QUALSIASI chiamata riporta al login.
      // Metterlo qui e non nei singoli schermi e' l'unico modo perche' valga
      // sempre: uno schermo che se ne dimentica lascia l'operatore davanti a
      // una pagina che non si aggiorna piu' senza dire perche'.
      onUnauthenticated: () => setPhase({ kind: "anonymous" }),
    });
  }
  const api = apiRef.current;

  const refresh = useCallback(async () => {
    // `auth.bootstrapStatus` prima di `auth.me`: se non esiste nessun utente,
    // chiedere chi siamo non ha senso e la risposta 401 manderebbe a una
    // schermata di login dove non si puo' entrare.
    try {
      const st = await api.call<{ needsBootstrap: boolean }>("auth.bootstrapStatus");
      if (st.needsBootstrap) {
        setPhase({ kind: "bootstrap" });
        return;
      }
    } catch {
      // Server irraggiungibile: si prosegue verso il login, che mostrera'
      // l'errore quando si prova a entrare.
      setPhase({ kind: "anonymous" });
      return;
    }
    try {
      const user = await api.call<User>("auth.me");
      setPhase({ kind: "authenticated", user });
    } catch {
      setPhase({ kind: "anonymous" });
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await api.call<{ user: User }>("auth.login", { username, password });
      setPhase({ kind: "authenticated", user: res.user });
    },
    [api],
  );

  const bootstrap = useCallback(
    async (username: string, password: string, displayName: string) => {
      const res = await api.call<{ user: User }>("auth.bootstrapAdmin", {
        username,
        password,
        displayName,
      });
      setPhase({ kind: "authenticated", user: res.user });
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.call("auth.logout");
    } catch (e) {
      // Uscire deve riuscire sempre. Se il server non risponde, il cookie
      // resta ma la pagina torna comunque al login: il contrario — restare
      // dentro perche' la rete e' caduta — e' peggio davanti a un impianto.
      if (!(e instanceof ApiError)) throw e;
    }
    setPhase({ kind: "anonymous" });
  }, [api]);

  const can = useCallback(
    (key: string) =>
      phase.kind === "authenticated" && phase.user.permissions?.[key] === true,
    [phase],
  );

  const value = useMemo<SessionValue>(
    () => ({ phase, api, login, bootstrap, logout, refresh, can }),
    [phase, api, login, bootstrap, logout, refresh, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession fuori da <SessionProvider>");
  return v;
}
